import React, { useState } from 'react';
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { ArrowRight, Sparkles, Check } from 'lucide-react';
import { createPreRegistration } from 'api/preRegistration';
import { motion } from 'framer-motion';

export default function HeroSection() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    setError(null);
    try {
      // Never claim success optimistically: a silent failure loses the lead
      // while telling the prospect we have it.
      await createPreRegistration({ email, source: 'hero' });
      setIsSubmitted(true);
      setEmail('');
    } catch (err) {
      setError(
        err?.response?.status === 429
          ? 'Слишком много запросов. Попробуйте позже.'
          : 'Не удалось отправить заявку. Попробуйте ещё раз.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="min-h-screen bg-[#f5f5f0] relative overflow-hidden">
      {/* Grid background */}
      <div className="absolute inset-0 opacity-30">
        <div className="w-full h-full" style={{
          backgroundImage: `
            linear-gradient(to right, #1a1a1a 1px, transparent 1px),
            linear-gradient(to bottom, #1a1a1a 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }} />
      </div>

      {/* Floating shapes */}
      <motion.div
        className="absolute top-20 right-20 w-32 h-32 bg-[#d4ff00] border-4 border-black rotate-12"
        animate={{ rotate: [12, -5, 12], y: [0, -20, 0] }}
        transition={{ duration: 6, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-40 left-20 w-24 h-24 bg-[#ff6b35] border-4 border-black rounded-full"
        animate={{ scale: [1, 1.1, 1], x: [0, 10, 0] }}
        transition={{ duration: 4, repeat: Infinity }}
      />
      <motion.div
        className="absolute top-1/3 left-1/4 w-16 h-16 bg-[#4361ee] border-4 border-black"
        animate={{ rotate: [0, 90, 0] }}
        transition={{ duration: 8, repeat: Infinity }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-2 bg-black text-[#d4ff00] px-4 py-2 border-4 border-black shadow-[6px_6px_0_0_#1a1a1a] mb-8"
        >
          <Sparkles className="w-4 h-4" />
          <span className="font-bold text-sm uppercase tracking-wider">Открыт ранний доступ</span>
        </motion.div>

        {/* Main headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-[clamp(1.75rem,8.8vw,3rem)] md:text-7xl lg:text-8xl font-black text-black leading-[0.9] mb-6 max-w-5xl"
        >
          Аналитика на
          <br />
          <span className="relative inline-block">
            автопилоте
            <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none">
              <path d="M2 8 Q 150 2 298 8" stroke="#d4ff00" strokeWidth="6" strokeLinecap="round"/>
            </svg>
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-xl md:text-2xl text-black/70 max-w-2xl mb-10 font-medium"
        >
          Аналитика уровня enterprise — без команды под неё. Ни дата-инженеров,
          ни аналитиков, ни возни с инфраструктурой: только готовые ответы.
        </motion.p>

        {/* Email form */}
        <motion.form
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          onSubmit={handleSubmit}
          className="flex flex-col sm:flex-row gap-4 max-w-2xl"
        >
          {!isSubmitted ? (
            <>
              <div className="flex-1 relative">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-14 text-lg bg-white border-4 border-black rounded-none shadow-[4px_4px_0_0_#1a1a1a] focus:shadow-[6px_6px_0_0_#1a1a1a] focus:translate-x-[-2px] focus:translate-y-[-2px] transition-all placeholder:text-black/40"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="h-14 px-7 text-lg font-bold whitespace-nowrap shrink-0 bg-[#d4ff00] text-black border-4 border-black rounded-none shadow-[4px_4px_0_0_#1a1a1a] hover:shadow-[6px_6px_0_0_#1a1a1a] hover:translate-x-[-2px] hover:translate-y-[-2px] hover:bg-[#c4ef00] transition-all disabled:opacity-50"
              >
                {isSubmitting ? 'Отправляем…' : 'Получить доступ'}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </>
          ) : (
            <div className="flex items-center gap-3 bg-black text-[#d4ff00] px-6 py-4 border-4 border-black">
              <Check className="w-6 h-6" />
              <span className="font-bold text-lg">Вы в списке! Скоро свяжемся.</span>
            </div>
          )}
        </motion.form>
        {error && (
          <p className="mt-3 font-bold text-red-700" role="alert">
            {error}
          </p>
        )}

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex flex-wrap gap-8 mt-16"
        >
          {[
            { value: '600+', label: 'Готовых коннекторов' },
            { value: '0', label: 'Ручной настройки' },
            { value: '100%', label: 'Ваши данные' },
          ].map((stat, i) => (
            <div key={i} className="text-center">
              <div className="text-4xl md:text-5xl font-black text-black">{stat.value}</div>
              <div className="text-sm font-bold text-black/60 uppercase tracking-wider">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
